import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Award, Leaf, Clock, CalendarCheck, Users, ArrowRight } from "lucide-react";
import { Seo, breadcrumbSchema, faqSchema, serviceSchema } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { ChecklistSection } from "../components/ChecklistSection";
import { ProcessSteps } from "../components/ProcessSteps";
import { FaqAccordion } from "../components/FaqAccordion";
import { ServiceCTA } from "../components/ServiceCTA";
import { boroughs, CHECKLIST_GROUPS, PROCESS } from "../data/boroughs";

const WHY_CHOOSE = [
  {
    icon: Shield,
    title: "Fully insured cleaners",
    description:
      "Every team member carries £5M public liability insurance — protecting you, your landlord and the property during the clean.",
  },
  {
    icon: Award,
    title: "7-day re-clean guarantee",
    description:
      "If your landlord or inventory clerk raises any cleaning issue within 7 days, we return and re-clean the flagged areas at no cost.",
  },
  {
    icon: Users,
    title: "Estate-agent standards",
    description:
      "Our checklist mirrors the AIIC inventory benchmark used by major London agencies, so the finish reads correctly on the checkout report.",
  },
  {
    icon: Leaf,
    title: "Eco-friendly products",
    description:
      "Plant-based, fragrance-free degreasers and descalers — safe around children, pets and sensitive landlords without sacrificing cleaning power.",
  },
  {
    icon: Clock,
    title: "Same-day availability",
    description:
      "Last-minute checkout? We routinely fit in same-day and next-day jobs at month-end when most London tenancies expire.",
  },
  {
    icon: CalendarCheck,
    title: "Weekend bookings",
    description:
      "Saturday, Sunday and public holiday slots are standard — perfect for tenants juggling work, removals and a checkout in the same week.",
  },
];

const buildFaqs = (borough) => [
  {
    question: `Which postcodes do you cover in ${borough.name}?`,
    answer: `We cover all ${borough.name} postcodes including ${borough.postcodes.join(", ")}, serving ${borough.areas}. If your property is just outside these postcodes, call us — we cover all 32 London boroughs.`,
  },
  {
    question: `How quickly can you clean my ${borough.name} property?`,
    answer: `We offer same-day and next-day bookings across ${borough.name}, particularly at month-end when most ASTs expire. Call us on 020 8036 3586 or message us on WhatsApp for immediate availability.`,
  },
  {
    question: `What does end of tenancy cleaning in ${borough.name} include?`,
    answer:
      "Our end of tenancy cleaning follows the standard inventory checklist used by London letting agents — every room is deep cleaned including kitchen appliances inside and out, bathroom limescale removal, internal windows, skirting boards, light fittings, switches, sockets and door tops.",
  },
  {
    question: "Do you offer a re-clean guarantee?",
    answer:
      "Yes. If your landlord or letting agent flags any cleaning issue within 7 days of our visit, we return and re-clean the flagged areas at no extra cost — the strongest written guarantee available from any tenancy cleaner in London.",
  },
  {
    question: "How much does end of tenancy cleaning cost?",
    answer:
      "Studio flats start from £99, one-bedroom from £149, two-bedroom from £179 and three-bedroom homes from £230. Add-ons such as carpet steam cleaning and oven dip-tank degreasing are priced separately. See our full prices page for the complete breakdown.",
  },
];

const NearbyBoroughs = ({ nearby }) => {
  if (!nearby.length) return null;
  return (
    <section className="py-16 md:py-20 bg-white">
      <div className="container mx-auto px-6 md:px-12 lg:px-20">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-navy mb-3">
          Nearby Areas We Also Cover
        </h2>
        <p className="text-slate-600 mb-8 max-w-2xl">
          We serve all 32 London boroughs. If your property is just across the
          border, we cover that area too.
        </p>
        <div className="flex flex-wrap gap-3">
          {nearby.map((b) => (
            <motion.div
              key={b.slug}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3 }}
            >
              <Link
                to={`/end-of-tenancy-cleaning-${b.slug}`}
                className="inline-flex items-center gap-1.5 bg-slate-50 hover:bg-navy hover:text-white border border-slate-200 text-slate-700 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
              >
                {b.name}
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </motion.div>
          ))}
          <Link
            to="/end-of-tenancy-cleaning-london"
            className="inline-flex items-center gap-1.5 bg-teal/10 hover:bg-teal hover:text-white border border-teal/20 text-teal text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
          >
            All London
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const BoroughPage = ({ borough }) => {
  const path = `/end-of-tenancy-cleaning-${borough.slug}`;
  const faqs = buildFaqs(borough);
  const nearby = boroughs.filter((b) => borough.nearby.includes(b.slug));

  return (
    <>
      <Seo
        title={`End of Tenancy Cleaning ${borough.name} | Prime Tenancy Cleaning`}
        description={`Professional end of tenancy cleaning in ${borough.name}. Inventory-ready, landlord approved, £5M insured. Covers ${borough.postcodes.join(", ")}. 7-day re-clean guarantee.`}
        ogTitle={`End of Tenancy Cleaning ${borough.name} | Prime Tenancy`}
        ogDescription={`Move-out cleaning in ${borough.name} (${borough.postcodes.slice(0, 3).join(", ")}). Fully insured, estate-agent approved. 7-day re-clean guarantee.`}
        path={path}
        schema={[
          breadcrumbSchema([
            { name: "End of Tenancy Cleaning London", path: "/end-of-tenancy-cleaning-london" },
            { name: `${borough.name}`, path },
          ]),
          faqSchema(faqs),
          serviceSchema({
            name: `End of Tenancy Cleaning ${borough.name}`,
            serviceType: "End of Tenancy Cleaning",
            description: `Inventory-grade end of tenancy cleaning across ${borough.name}, covering ${borough.postcodes.join(", ")}.`,
            areaServed: borough.name,
          }),
        ]}
      />

      <PageHero
        eyebrow={`${borough.name} · End of Tenancy Cleaning`}
        h1={`End of Tenancy Cleaning`}
        highlight={`in ${borough.name}`}
        subheading={`Inventory-check ready, landlord approved and backed by a 7-day re-clean guarantee. Serving ${borough.postcodes.join(", ")} and all ${borough.name} postcodes.`}
        breadcrumbs={[
          { name: "End of Tenancy Cleaning London", path: "/end-of-tenancy-cleaning-london" },
          { name: borough.name, path },
        ]}
        image="https://images.unsplash.com/photo-1521012108593-cf7f7c0fbb89?w=1920&q=80"
      />

      {/* Borough-specific intro */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl">
            <span className="text-teal font-semibold text-sm uppercase tracking-wider">
              {borough.name} Coverage
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-navy mt-3 mb-6 leading-tight">
              End of tenancy cleaning across {borough.name}
            </h2>
            <div className="text-slate-700 text-base md:text-lg leading-relaxed space-y-5">
              <p>{borough.intro}</p>
              <p>
                Every clean we carry out in {borough.name} follows the same
                inventory-grade checklist used by London letting agents at
                checkout — the same document AIIC-trained inventory clerks
                inspect against. We back the work with a written{" "}
                <strong>7-day re-clean guarantee</strong>: if your landlord
                raises any cleaning issue within seven days, we return and
                re-clean the flagged areas at no cost.
              </p>
              <p>
                See our full{" "}
                <Link to="/prices" className="text-teal font-medium hover:text-navy">
                  end of tenancy cleaning prices
                </Link>
                , or visit the{" "}
                <Link
                  to="/end-of-tenancy-cleaning-london"
                  className="text-teal font-medium hover:text-navy"
                >
                  main London service page
                </Link>{" "}
                for the complete service overview.
              </p>
            </div>

            {/* Postcode tags */}
            <div className="flex flex-wrap gap-2 mt-8">
              {borough.postcodes.map((pc) => (
                <span
                  key={pc}
                  className="bg-navy/5 text-navy font-semibold text-sm px-3 py-1.5 rounded-lg"
                >
                  {pc}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <ChecklistSection
        eyebrow="What's Included"
        heading="The complete end of tenancy cleaning checklist"
        subheading="Every property in any London borough gets the same inventory-grade specification. Add-ons such as carpet steam cleaning and oven dip-tank degreasing are priced separately."
        groups={CHECKLIST_GROUPS}
        background="slate"
      />

      <ProcessSteps
        eyebrow="The Process"
        heading="From quote to clean in four steps"
        subheading={`Most ${borough.name} tenants book us between five and ten days before their checkout — but if you need us tomorrow, we can usually make it work.`}
        steps={PROCESS}
        background="white"
      />

      <FaqAccordion
        heading={`End of tenancy cleaning ${borough.name} FAQs`}
        subheading={`Common questions from tenants and landlords in ${borough.name} about move-out cleans.`}
        faqs={faqs}
        background="slate"
      />

      <NearbyBoroughs nearby={nearby} />

      <ServiceCTA
        title={`Book your ${borough.name} end of tenancy clean`}
        description="Get a fixed-price quote with no hidden fees, or call our London team and we'll talk through your checkout in minutes."
        variant="navy"
      />
    </>
  );
};

export default BoroughPage;
